import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { LOGO_URL } from '../context/NotificationContext';

export default function LoginPage() {
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('player');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name, role);
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const demoAccounts = [
    { label: 'Player', email: 'marco.santos@email.com', password: 'player123', icon: '🏀' },
    { label: 'Admin', email: 'admin@setplay.io', password: 'admin123', icon: '⚡' },
    { label: 'Coach', email: 'coach@setplay.io', password: 'coach123', icon: '📋' },
    { label: 'Venue', email: 'venue@setplay.io', password: 'venue123', icon: '🏟' },
  ];

  return (
    <div className="min-h-screen bg-[#020408] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background noise & glow */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWx0ZXI9InVybCgjYSkiIG9wYWNpdHk9IjAuMDMiLz48L3N2Zz4=')] opacity-50" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <img src={LOGO_URL} alt="SetPlay" className="w-14 h-14 rounded-xl shadow-[0_0_20px_rgba(59,130,246,0.3)]" />
            <span className="font-barlow text-3xl font-bold text-white tracking-tight">SetPlay</span>
          </div>
          <p className="text-slate-400 text-sm font-manrope">Agentic Incentives & Token Economy</p>
        </div>

        {/* Card */}
        <div className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl">
          <h2 className="font-barlow text-xl font-bold text-white mb-1" data-testid="auth-title">
            {isRegister ? 'Create Account' : 'Welcome Back'}
          </h2>
          <p className="text-slate-400 text-sm mb-6 font-manrope">
            {isRegister ? 'Join the $PLAY rewards economy' : 'Sign in to your rewards dashboard'}
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Name</Label>
                  <Input
                    data-testid="register-name-input"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your name"
                    className="mt-1 bg-slate-900/50 border-slate-700 focus:border-blue-500 text-white placeholder:text-slate-500"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Role</Label>
                  <select
                    data-testid="register-role-select"
                    value={role}
                    onChange={e => setRole(e.target.value)}
                    className="mt-1 w-full rounded-lg bg-slate-900/50 border border-slate-700 text-white p-2 text-sm focus:border-blue-500 outline-none"
                  >
                    <option value="player">Player</option>
                    <option value="coach">Coach</option>
                    <option value="venue_owner">Venue Owner</option>
                  </select>
                </div>
              </>
            )}

            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="mt-1 bg-slate-900/50 border-slate-700 focus:border-blue-500 text-white placeholder:text-slate-500"
                required
              />
            </div>

            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="mt-1 bg-slate-900/50 border-slate-700 focus:border-blue-500 text-white placeholder:text-slate-500 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold tracking-wide uppercase text-sm shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Loading...' : (isRegister ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              data-testid="auth-toggle-btn"
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Register"}
            </button>
          </div>

          {/* Demo accounts */}
          {!isRegister && (
            <div className="mt-6 pt-4 border-t border-white/5">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 text-center">Quick Demo Access</p>
              <div className="flex gap-2">
                {demoAccounts.map(d => (
                  <button
                    key={d.email}
                    data-testid={`demo-${d.label.split(' ')[0].toLowerCase()}-btn`}
                    onClick={() => { setEmail(d.email); setPassword(d.password); }}
                    className="flex-1 py-2 px-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 text-xs text-slate-300 hover:text-white transition-all text-center"
                  >
                    <span className="block text-base mb-0.5">{d.icon}</span>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
